import { animated, useSpring } from '@react-spring/web';
import type { FC, MouseEvent } from 'react';
import {
  Box,
  Column,
  Container,
  Row,
  Section,
  Spacer,
  Stack,
} from 'src/components-v2/Layout';
import useGradientText from 'src/hooks/useGradientText';
import styled from 'threads5/styles/styled';
import Ben from './Ben';
import Heading1 from './Heading';
import HeroCta from './HeroCta';
import HeroMarquee from './HeroMarquee';
import Subheading from './Subheading';
import Thumbprint from './Thumbprint';
import Toggle, { useToggleContext } from './Toggle';

const SectionHeader = styled(Section, {
  shouldForwardProp: (prop) => {
    return prop !== 'haveAllCriticalAssetsLoaded';
  },
})<{
  onMouseMove?: (e: MouseEvent) => void;
  haveAllCriticalAssetsLoaded?: boolean;
}>(({ theme, haveAllCriticalAssetsLoaded }) => {
  return {
    position: 'relative',
    background:
      'linear-gradient(315deg, #07578C 25.85%, #0E94D8 63.18%, #42F0CD 98.91%)',
    overflow: 'hidden',
    overflowX: 'hidden',
    cursor: haveAllCriticalAssetsLoaded ? 'default' : 'wait',
    [theme.breakpoints.up('xs')]: {
      height: '864px',
    },
    [theme.breakpoints.up('sm')]: {
      height: '872px',
      background:
        'linear-gradient(-210deg, #07578C 27.07%, #0E94D8 59.71%, #42F0CD 92.45%)',
    },
    [theme.breakpoints.up('md')]: {
      height: '923px',
    },
    [theme.breakpoints.up('xl')]: {
      height: '929px',
    },
  };
});

const Hero: FC = () => {
  const { isPsychedelic } = useToggleContext();
  const { elementRef, handleMouseMove, parentRef } = useGradientText<
    HTMLHeadingElement,
    HTMLDivElement
  >({
    gradientColors: isPsychedelic
      ? '#E5D3F8 9.35%, #10D0B7 59.18%, #97D091 101.62%'
      : '#86EF5A 0.86%, #10D0B7 38.09%, #FFFFFF 69.81%',
  });
  const { opacity } = useSpring({
    opacity: isPsychedelic ? 1 : 0,
  });

  return (
    <SectionHeader
      ref={parentRef}
      onMouseMove={handleMouseMove}
      component='header'
      haveAllCriticalAssetsLoaded={true}
    >
      <animated.div
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          background: 'linear-gradient(95deg, #01172E 27.81%, #012E37 76.6%)',
          opacity,
        }}
      />
      <Container sx={{ height: '100%', mt: '7px' }}>
        <Row
          sx={{ height: { xs: 'auto', md: '100%' } }}
          direction={{ xs: 'column-reverse', sm: 'row' }}
        >
          <Column xs={22} xl={13} xsOffset={1}>
            <Stack direction='column'>
              <Box
                sx={(theme) => {
                  return {
                    [theme.breakpoints.up('md')]: {
                      height: '270px',
                    },
                    [theme.breakpoints.down('md')]: {
                      height: '135px',
                    },
                    [theme.breakpoints.down('sm')]: {
                      height: '432px',
                    },
                    [theme.breakpoints.down(440)]: {
                      height: '390px',
                    },
                  };
                }}
              />
              {isPsychedelic ? (
                <Heading1
                  key={'psychedelic'}
                  ref={elementRef}
                  sx={{
                    background:
                      'radial-gradient(circle at top left, #E5D3F8 9.35%, #10D0B7 59.18%, #97D091 101.62%)',
                  }}
                />
              ) : (
                <Heading1
                  key={'default'}
                  ref={elementRef}
                  sx={{
                    background:
                      'radial-gradient(circle at top left, #86EF5A 0.86%, #10D0B7 38.09%, #FFFFFF 69.81%)',
                  }}
                />
              )}
              <Subheading />
              <Spacer sx={{ height: { xs: '20px', sm: '44px', md: '50px' } }} />
              <Toggle
                sx={(theme) => {
                  return {
                    display: 'flex',
                    a: {
                      [theme.breakpoints.down('sm')]: {
                        width: '100%',
                      },
                    },
                  };
                }}
              />
              <Spacer
                sx={{ height: { xs: '32px' }, display: { sm: 'none' } }}
              />
              <HeroMarquee />
              <HeroCta />
            </Stack>
          </Column>
          <Column
            className='hide-for-aeo'
            xl={10}
            sx={{
              position: { xs: 'static', xl: 'relative' },
            }}
          >
            <Ben />
          </Column>
        </Row>
      </Container>
      <Thumbprint key='hero' />
    </SectionHeader>
  );
};

export default Hero;
